import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const waitForSearchStyles = (link, signal) => {
  if (signal.aborted) return Promise.reject(new Error('Search disconnected'));
  if (link.dataset.loadError) return Promise.reject(new Error('Search stylesheet failed to load'));
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      link.removeEventListener('load', loaded);
      link.removeEventListener('error', failed);
      signal.removeEventListener('abort', failed);
    };
    const loaded = () => { cleanup(); resolve(); };
    const failed = () => { cleanup(); reject(new Error('Search stylesheet unavailable')); };
    link.addEventListener('load', loaded, { once: true });
    link.addEventListener('error', failed, { once: true });
    signal.addEventListener('abort', failed, { once: true });
  });
};

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#searchStylesAbort?.abort();
    this.#searchStylesAbort = null;
    this.#searchOpenAttempt++;
    this.#searchOpening = false;
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {HTMLElement | null} */
  #searchOpener = null;
  #searchOpening = false;
  #searchStylesAbort = null;
  #searchOpenAttempt = 0;

  /**
   * Shows the dialog.
   * @param {Event} [event] - Declarative activation, resolved to the opener by Component.
   */
  showDialog(event) {
    const { dialog } = this.refs;

    if (dialog.open || (this.id === 'search-modal' && this.#searchOpening)) return;

    if (this.id === 'search-modal') {
      // Safari pointer activation can leave activeElement on the page instead of the Search button.
      const opener = event?.target instanceof HTMLElement ? event.target : document.activeElement;
      this.#searchOpener = opener instanceof HTMLElement ? opener : null;
      this.#searchOpening = true;
      const attempt = ++this.#searchOpenAttempt;
      const stylesheetId = this.getAttribute('data-stylesheet-id');
      const stylesheet = stylesheetId ? document.getElementById(stylesheetId) : null;
      if (stylesheet && !stylesheet.sheet) {
        const controller = new AbortController();
        this.#searchStylesAbort = controller;
        waitForSearchStyles(stylesheet, controller.signal).then(() => {
          if (controller.signal.aborted || !this.isConnected || attempt !== this.#searchOpenAttempt) return;
          stylesheet.media = 'all';
          this.#showDialog(attempt);
        }).catch((error) => {
          if (attempt !== this.#searchOpenAttempt) return;
          this.#searchStylesAbort = null;
          this.#searchOpening = false;
          if (!controller.signal.aborted) console.error(error);
        });
        return;
      }
      if (stylesheet) stylesheet.media = 'all';
      this.#showDialog(attempt);
      return;
    }
    this.#showDialog();
  }

  #showDialog(attempt) {
    const { dialog } = this.refs;
    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (attempt !== undefined && attempt !== this.#searchOpenAttempt) return;
      this.#searchStylesAbort = null;
      this.#searchOpening = false;
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (this.id === 'search-modal') {
      const opener = this.#searchOpener;
      this.#searchOpener = null;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          // Native details can open before the header controller or its styles.
          // Keep its panel closed until the same readiness gate used by clicks.
          const drawer = event.target.closest('header-drawer');
          if (drawer?.headerStylesReady === false) {
            drawer.prepareNativeOpen(event.target);
            return;
          }
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
